/**
 * CORE-1114 — оттенки и прозрачность одного цвета.
 *
 * Поверхности, крашенные акцентом (плитки быстрых действий и им подобные),
 * заданы каждая своим цветом, и производные — темнее, светлее, полупрозрачный
 * — считаются из него. Вторая палитра из готовых пар разъехалась бы с первой
 * на первом же новом цвете.
 */

/** `#RGB` и `#RRGGBB` → [r, g, b]. Неразобранное отдаём как чёрный. */
function parseHex(hex: string): [number, number, number] {
  const clean = hex.replace('#', '').trim();
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  if (full.length !== 6) return [0, 0, 0];
  const value = parseInt(full, 16);
  if (Number.isNaN(value)) return [0, 0, 0];
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
const toHex = (n: number) => clamp(n).toString(16).padStart(2, '0');

/**
 * Смешивает цвет с белым (`amount > 0`) или чёрным (`amount < 0`).
 *
 * Смешивание, а не умножение яркости: у тёмных цветов умножение почти ничего
 * не меняет (0.8 от 28 — всё те же 22), и градиент на красной плитке вышел бы
 * незаметным. Доля — от 0 до 1, то есть 0.2 = «на пятую часть к белому».
 */
export function shadeHex(hex: string, amount: number): string {
  const [r, g, b] = parseHex(hex);
  const target = amount >= 0 ? 255 : 0;
  const k = Math.abs(amount);
  const mix = (c: number) => c + (target - c) * k;
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}


/**
 * Тот же цвет с заданной прозрачностью.
 *
 * CORE-1178 — панель быстрых действий строит из акцента строки и заливку 7%,
 * и рамку 22%, и подсветку иконки. Держать под каждый процент отдельную
 * константу в палитре значит переписывать четыре строки на каждое изменение
 * оттенка; `color-mix` из макета react-native-web не понимает.
 */
export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Относительная яркость WCAG 2.x для `#RRGGBB`. */
function luminance(hex: string): number {
  const lin = (c: number) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  const [r, g, b] = parseHex(hex);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Контраст двух непрозрачных цветов по WCAG 2.x (1…21). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Цвет `hex` с прозрачностью `alpha`, положенный на непрозрачный `under`. */
export function blendOver(hex: string, alpha: number, under: string): string {
  const [r, g, b] = parseHex(hex);
  const [ur, ug, ub] = parseHex(under);
  const mix = (c: number, u: number) => c * alpha + u * (1 - alpha);
  return `#${toHex(mix(r, ur))}${toHex(mix(g, ug))}${toHex(mix(b, ub))}`;
}

/**
 * CORE-1733 (возврат QA) — ближайший к акценту оттенок (к чёрному на светлой
 * подложке, к белому на тёмной), который держит `min` на подложке `under`. Подпись остаётся «своего» цвета, но
 * читается: салатовый #A3E635 при прежнем фиксированном −42% давал 4.07:1.
 */
export function inkOn(accent: string, under: string, min = 4.6, toward: 'dark' | 'light' = 'dark'): string {
  const sign = toward === 'dark' ? -1 : 1;
  if (contrastRatio(accent, under) >= min) return accent;
  for (let k = 0.02; k <= 0.9; k += 0.02) {
    const ink = shadeHex(accent, sign * k);
    if (contrastRatio(ink, under) >= min) return ink;
  }
  return shadeHex(accent, sign * 0.9);
}
